import { escapeHtml } from './helpers';
import { escapeHtmlAttr, resolveEmailImageUrl } from './emailAssetUrl';
import {
  HERO_PRESET_IMAGES,
  isHeroCoreTemplate,
  normalizeHeroButtonRadius,
  resolveHeroContentStyles,
  type HeroContentTemplate,
} from './heroContentUtils';
import { renderHeroContentExtendedHtml } from './heroContentExtendedHtml';

const LEGACY_HERO_IMAGE_URLS: Record<string, string> = {
  'https://images.unsplash.com/photo-1522202222206-72cc1c482f5a?auto=format&fit=crop&w=1200&q=80':
    HERO_PRESET_IMAGES.split,
  'https://images.unsplash.com/photo-1522202222206-72cc1c482f5a':
    HERO_PRESET_IMAGES.split,
};

function resolveHeroEmailImage(raw: string, assetOrigin?: string): string {
  const trimmed = raw.trim();
  const migrated = LEGACY_HERO_IMAGE_URLS[trimmed] || trimmed;
  return resolveEmailImageUrl(migrated, assetOrigin);
}

export type HeroContentBlockContent = Record<string, unknown>;
export type HeroContentBlockStyles = Record<string, unknown>;

const buttonRadiusCss = (
  content: HeroContentBlockContent,
  r: ReturnType<typeof resolveHeroContentStyles>
) => normalizeHeroButtonRadius(content.buttonBorderRadius, r.buttonRadius);

const renderButtonLink = (
  content: HeroContentBlockContent,
  r: ReturnType<typeof resolveHeroContentStyles>,
  padding = '12px 24px'
): string => {
  const radius = buttonRadiusCss(content, r);
  const isOutline = content.buttonStyle === 'outline';
  const isGhost = content.buttonStyle === 'ghost';
  const bg = isOutline || isGhost ? 'transparent' : r.buttonBg;
  const color = isOutline || isGhost ? r.buttonBg : r.buttonColor;
  const border = isOutline ? `border: 1px solid ${r.buttonBg};` : '';
  const decoration = isGhost ? 'text-decoration: underline;' : 'text-decoration: none;';
  return `<a href="${escapeHtml(String(content.buttonUrl || '#'))}" class="hero-content-button" style="display: inline-block; background-color: ${bg}; color: ${color}; ${border} padding: ${escapeHtml(String(content.buttonPadding || padding))}; border-radius: ${radius} !important; font-size: ${r.buttonFontSize}; font-weight: 600; ${decoration} font-family: ${r.fontFamily};">${escapeHtml(String(content.buttonText))}</a>`;
};

const renderButton = (
  content: HeroContentBlockContent,
  r: ReturnType<typeof resolveHeroContentStyles>,
  align: string
): string => {
  if (!content.buttonText || content.showButton === false) return '';
  return `<div class="hero-content-button-wrap" style="text-align: ${align};">${renderButtonLink(content, r)}</div>`;
};

export const renderHeroContentBlockHtml = (
  content: HeroContentBlockContent,
  styles: HeroContentBlockStyles,
  fontFamily: string,
  isMobile = false,
  assetOrigin?: string
): string | null => {
  const template = (content.template as string) || 'standard';
  if (!isHeroCoreTemplate(template)) return null;

  const r = resolveHeroContentStyles(template, content, styles, fontFamily, isMobile);
  const img = content.image ? resolveHeroEmailImage(String(content.image), assetOrigin) : '';
  const wrapPad = styles.padding || '0';

  if (template === 'standard') {
    const imageHtml = img
      ? `<div style="padding: 0; background-color: #F3F4F6; text-align: center;"><img src="${escapeHtmlAttr(img)}" alt="Hero" class="hero-content-image" style="width: 100%; height: auto; display: block; border: 0;" /></div>`
      : '';
    const heading = content.heading
      ? `<div style="padding: ${r.headingPadding}; background-color: ${r.contentBg};"><h1 class="hero-content-heading" style="margin: 0 0 20px 0; font-size: ${r.headingSize}; font-weight: 700; color: ${r.headingColor}; font-family: ${r.fontFamily}; line-height: 1.2;">${escapeHtml(String(content.heading))}</h1></div>`
      : '';
    const text = content.text
      ? `<div style="padding: ${r.textPadding}; background-color: ${r.contentBg};"><p class="hero-content-text" style="margin: 0; font-size: ${r.textSize}; color: ${r.textColor}; line-height: 160%; font-family: ${r.fontFamily};">${escapeHtml(String(content.text))}</p></div>`
      : '';
    const button =
      content.buttonText && content.showButton !== false
        ? `<div style="padding: ${r.buttonSectionPadding}; background-color: ${r.contentBg};">${renderButton(content, r, isMobile ? 'center' : r.buttonAlign)}</div>`
        : '';
    return `<div class="hero-content-wrap hero-content-standard" style="padding: ${wrapPad}; background-color: ${r.containerBg}; font-family: ${r.fontFamily}; width: 100%; box-sizing: border-box;">${imageHtml}${heading}${text}${button}</div>`;
  }

  if (template === 'dark-centered') {
    const heading = content.heading
      ? `<div style="padding: ${r.headingPadding};"><h1 class="hero-content-heading" style="margin: 0; font-size: ${r.headingSize}; font-weight: 700; color: ${r.headingColor}; font-family: ${r.fontFamily}; text-align: center; line-height: 1.2;">${escapeHtml(String(content.heading))}</h1></div>`
      : '';
    const text = content.text
      ? `<div style="padding: ${r.textPadding};"><p class="hero-content-text" style="margin: 0; font-size: ${r.textSize}; color: ${r.textColor}; line-height: 160%; font-family: ${r.fontFamily}; text-align: center;">${escapeHtml(String(content.text))}</p></div>`
      : '';
    const button =
      content.buttonText && content.showButton !== false
        ? `<div style="padding: ${r.buttonSectionPadding};">${renderButton(content, r, 'center')}</div>`
        : '';
    return `<div class="hero-content-wrap hero-content-dark-centered" style="padding: ${wrapPad}; background-color: ${r.containerBg}; font-family: ${r.fontFamily}; width: 100%; box-sizing: border-box; text-align: center;">${heading}${text}${button}</div>`;
  }

  if (template === 'split') {
    const heading = content.heading
      ? `<h1 class="hero-content-heading" style="margin: 0 0 12px 0; font-size: ${r.headingSize}; font-weight: 700; color: ${r.headingColor}; font-family: ${r.fontFamily}; line-height: 1.2;">${escapeHtml(String(content.heading))}</h1>`
      : '';
    const text = content.text
      ? `<p class="hero-content-text" style="margin: 0 0 20px 0; font-size: ${r.textSize}; color: ${r.textColor}; line-height: 160%; font-family: ${r.fontFamily};">${escapeHtml(String(content.text))}</p>`
      : '';
    const button =
      content.buttonText && content.showButton !== false
        ? renderButtonLink(content, r, '11px 24px')
        : '';
    const splitImgMinH = isMobile ? 160 : 220;
    const imageHtml = img
      ? `<img src="${escapeHtmlAttr(img)}" alt="Hero" class="hero-content-image hero-split-image" style="width: 100%; min-height: ${splitImgMinH}px; height: auto; max-height: none; display: block; border: 0; object-fit: cover; object-position: center center;" />`
      : `<div class="hero-split-image-placeholder" style="width: 100%; min-height: ${splitImgMinH}px; background-color: ${r.splitImageBg};"></div>`;

    if (isMobile) {
      return `
        <div class="hero-content-wrap hero-content-split" style="padding: ${wrapPad}; background-color: ${r.containerBg}; font-family: ${r.fontFamily}; width: 100%; box-sizing: border-box;">
          <table class="hero-split-table" role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
            <tr>
              <td class="hero-split-stack-cell" style="padding: ${r.splitImagePadding}; background-color: ${r.splitImageBg}; text-align: center; vertical-align: middle;">${imageHtml}</td>
            </tr>
            <tr>
              <td class="hero-split-stack-cell" style="padding: ${r.splitTextPadding}; background-color: ${r.contentBg}; vertical-align: middle;">${heading}${text}${button}</td>
            </tr>
          </table>
        </div>`;
    }

    return `
      <table class="hero-content-wrap hero-content-split hero-split-table" role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="padding: ${wrapPad}; background-color: ${r.containerBg}; width: 100%; max-width: 100%; table-layout: fixed; box-sizing: border-box; font-family: ${r.fontFamily};">
        <tr>
          <td style="width: 50%; max-width: 50%; padding: ${r.splitTextPadding}; background-color: ${r.contentBg}; vertical-align: middle; box-sizing: border-box;">
            ${heading}${text}${button}
          </td>
          <td style="width: 50%; max-width: 50%; padding: ${r.splitImagePadding}; background-color: ${r.splitImageBg}; text-align: center; vertical-align: middle; box-sizing: border-box;">
            ${imageHtml}
          </td>
        </tr>
      </table>`;
  }

  const EXTENDED: HeroContentTemplate[] = [
    'centered-light',
    'banner-image',
    'accent-line',
    'overlay-minimal',
  ];
  if (EXTENDED.includes(template)) {
    return renderHeroContentExtendedHtml(
      template as 'centered-light' | 'banner-image' | 'accent-line' | 'overlay-minimal',
      content,
      styles,
      fontFamily,
      isMobile,
      assetOrigin
    );
  }

  // minimal
  const heading = content.heading
    ? `<div style="padding: ${r.minimalHeadingPadding};"><h1 class="hero-content-heading" style="margin: 0; font-size: ${r.headingSize}; font-weight: 700; color: ${r.headingColor}; text-align: ${r.textAlign}; font-family: ${r.fontFamily}; line-height: 1.2;">${escapeHtml(String(content.heading))}</h1></div>`
    : '';
  const text = content.text
    ? `<div style="padding: ${r.minimalTextPadding};"><p class="hero-content-text" style="margin: 0; font-size: ${r.textSize}; color: ${r.textColor}; line-height: 160%; text-align: ${r.textAlign}; font-family: ${r.fontFamily};">${escapeHtml(String(content.text))}</p></div>`
    : '';
  const button =
    content.buttonText && content.showButton !== false
      ? `<div style="padding: 0 20px 32px; text-align: ${r.textAlign};">${renderButton(content, r, r.buttonAlign)}</div>`
      : '';

  return `<div class="hero-content-wrap hero-content-minimal" style="padding: ${wrapPad}; background-color: ${r.containerBg}; font-family: ${r.fontFamily}; width: 100%; box-sizing: border-box;">${heading}${text}${button}</div>`;
};
